import { Plus, Type } from 'lucide-react'
import { TextTrackLanes } from './TextTrackLanes'
import { TextInspector } from './TextInspector'
import { addText, updateText, deleteText, duplicateText, textTrackItems, textTrackLanes, textInspectorDefaults, TEXT_LANE_HEIGHT, type TextPatch } from './textTrack'
import { forwardRef, useImperativeHandle, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { EditSpecDict, EditSpecClipDict } from '../../../../generated/nodeDataTypes'
import { writeVideoEditorEditSpec } from '../../../../api/client'
import { createNadDebug } from '../../../../utils/debugLog'
import { TimelineEditor } from './TimelineEditor'
import { fingerprintDreamwriteEditSpec } from './dreamvibeAdapter'
import { projectEditSpecTimeline, reorderEditSpecClip, splitEditSpecClip, updateEditSpecClip } from './editSpecTimeline'

const log = createNadDebug('dw:text-track')
const inputClass = 'w-full rounded-lg border border-input-border bg-input-bg px-2 py-1 text-sm text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20'

type InspectorDraft = { kind: 'text'; key: string; patch: TextPatch } | { kind: 'clip'; id: string; patch: Partial<EditSpecClipDict> }

export interface EditSpecTimelineEditorHandle {

  beginTextGesture: () => void
  finishTextGesture: (key: string, patch: TextPatch | null, revision: number) => void
}
interface Props {
  selectedTextKey: string | null
  onSelectText: (key: string | null) => void
  projectId: string
  canvasId: string
  nodeId: string
  editSpec: EditSpecDict
  revision: number
  sources: Readonly<Record<string, string>>
  sourceDurations?: Readonly<Record<string, number>>
  readOnly: boolean
  playheadPosition: number
  isPlaying: boolean
  onSetPlayhead: (seconds: number) => void
  zoomLevel: number
  onSetZoom: (level: number) => void
  onSpecCommitted: (spec: EditSpecDict, revision: number) => void
  onSavingChange?: (saving: boolean) => void
  inspectorTarget: HTMLElement | null
}

export const EditSpecTimelineEditor = forwardRef<EditSpecTimelineEditorHandle, Props>(function EditSpecTimelineEditor({ selectedTextKey, onSelectText, projectId, canvasId, nodeId, editSpec, revision, sources, sourceDurations,
  readOnly, playheadPosition, isPlaying, onSetPlayhead, onSpecCommitted, inspectorTarget, zoomLevel, onSetZoom, onSavingChange }: Props, ref) {
  const { t } = useTranslation('canvas')
  const [selectedClipIds, setSelection] = useState<Set<string>>(() => new Set())
  const [saving, setSaving] = useState(false)
  const [trimPreview, setTrimPreview] = useState<{ id: string; start: number; end: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [past, setPast] = useState<EditSpecDict[]>([])
  const [future, setFuture] = useState<EditSpecDict[]>([])
  const pending = useRef(false)
  const textGesture = useRef<{ revision: number; drafts: InspectorDraft[] } | null>(null)
  const ownRevision = useRef(revision)
  const latestRevision = useRef(revision)
  latestRevision.current = revision
  const state = useMemo(() => projectEditSpecTimeline(editSpec, sources, {
    selectedClipIds, zoomLevel, playheadPosition, isPlaying,
  }, sourceDurations), [editSpec, sources, sourceDurations, selectedClipIds, zoomLevel, playheadPosition, isPlaying])
  const previewState = useMemo(() => {
    const clip = state.clips.find((item) => item.id === trimPreview?.id)
    if (!trimPreview || !clip) return state
    const previewSpec = updateEditSpecClip(editSpec, clip.id, {
      in_seconds: trimPreview.start, out_seconds: clip.originalDuration - trimPreview.end,
    })
    return projectEditSpecTimeline(previewSpec, sources, state, sourceDurations)
  }, [state, trimPreview, editSpec, sources, sourceDurations])
  const selectedId = [...selectedClipIds][0]
  const selected = editSpec.clips.find((clip) => clip.id === selectedId)
  const locked = readOnly || saving

  useEffect(() => {
    if (textGesture.current && textGesture.current.revision !== revision) {
      textGesture.current = null
      setError(t('textTrack.changedRemotely'))
      log({ event: 'text-gesture-invalidated', nodeId, revision })
    }
    if (revision !== ownRevision.current) {
      setPast([])
      setFuture([])
      ownRevision.current = revision
    }
    log({ event: 'timeline-spec-observed', nodeId, revision, clipCount: editSpec.clips.length, readOnly })
  }, [nodeId, revision, editSpec, readOnly, t])

  useEffect(() => {
    if (selectedTextKey) setSelection(new Set())
  }, [selectedTextKey])

  async function commit(next: EditSpecDict, history: { past: EditSpecDict[]; future: EditSpecDict[] } = { past: [...past, editSpec].slice(-50), future: [] }) {
    if (readOnly || pending.current) return
    pending.current = true
    setSaving(true)
    onSavingChange?.(true)
    setError(null)
    try {
      const expectedFingerprint = await fingerprintDreamwriteEditSpec(editSpec)
      const result = await writeVideoEditorEditSpec(projectId, nodeId, {
        editSpec: next, expectedRevision: revision, expectedFingerprint, canvasId,
      })
      // A newer Director write may arrive while the HTTP acknowledgement is in flight.
      if (latestRevision.current <= result.revision) {
        ownRevision.current = result.revision
        setPast(history.past)
        setFuture(history.future)
        onSpecCommitted(next, result.revision)
      }
      log({ event: 'timeline-manual-edit-committed', nodeId, revision: result.revision })
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause)
      setError(message)
      log({ event: 'timeline-manual-edit-failed', nodeId, revision, error: message })
    } finally {
      pending.current = false
      setSaving(false)
      onSavingChange?.(false)
    }
  }

  function mutate(mutation: () => EditSpecDict) {
    if (locked) return
    try { void commit(mutation()) } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    }
  }
  function updateClip(id: string, patch: Partial<EditSpecClipDict>) {
    if (textGesture.current) { textGesture.current.drafts.push({ kind: 'clip', id, patch }); return }
    mutate(() => updateEditSpecClip(editSpec, id, patch))
  }
  function deleteSelected() {
    mutate(() => {
      const clips = editSpec.clips.filter((clip) => !selectedClipIds.has(clip.id))
      if (!clips.length) throw new Error(t('timelineDirector.keepOneClip'))
      return { ...editSpec, clips, audio: editSpec.audio && { ...editSpec.audio,
        tracks: editSpec.audio.tracks?.filter((track) => !selectedClipIds.has(track.id)) } }
    })
  }
  const overlays = [
    ...(editSpec.overlay_clips ?? []).map((overlay) => ({ ...overlay, label: overlay.component_kind ?? overlay.role })),
  ]

  const textItems = textTrackItems(editSpec)
  const textLanes = textTrackLanes(textItems)
  const selectedText = textItems.find((item) => item.key === selectedTextKey)
  const changeText = (key: string, patch: TextPatch, baseRevision = revision) => {
    if (baseRevision !== revision) {
      setError(t('textTrack.changedRemotely'))
      log({ event: 'text-gesture-rejected', nodeId, textKey: key, baseRevision, revision })
      return
    }
    if (textGesture.current) { textGesture.current.drafts.push({ kind: 'text', key, patch }); return }
    log({ event: 'text-change-requested', projectId, canvasId, nodeId, textKey: key, fields: Object.keys(patch), revision })
    mutate(() => updateText(editSpec, key, patch))
  }
  const beginTextGesture = () => { textGesture.current = { revision, drafts: [] } }
  const finishTextGesture = (key: string, patch: TextPatch | null, baseRevision: number) => {
    const gesture = textGesture.current
    textGesture.current = null
    if (baseRevision !== revision || (gesture && gesture.revision !== revision)) {
      setError(t('textTrack.changedRemotely'))
      log({ event: 'text-gesture-rejected', nodeId, textKey: key, baseRevision, revision })
      return
    }
    const drafts = gesture?.drafts ?? []
    if (!patch && !drafts.length) return
    log({ event: 'text-gesture-commit', nodeId, textKey: key, revision, inspectorDrafts: drafts.length })
    mutate(() => {
      const next = drafts.reduce((value, draft) => draft.kind === 'text' ? updateText(value, draft.key, draft.patch) : updateEditSpecClip(value, draft.id, draft.patch), editSpec)
      return patch ? updateText(next, key, patch) : next
    })
  }
  useImperativeHandle(ref, () => ({ beginTextGesture, finishTextGesture }))
  const selectText = (key: string) => {
    setSelection(new Set())
    onSelectText(key)
    log({ event: 'text-selected', projectId, nodeId, textKey: key, playheadPosition })
    const item = textItems.find((item) => item.key === key)
    if (item && (playheadPosition < item.start_seconds || playheadPosition >= item.start_seconds + item.duration_seconds)) onSetPlayhead(item.start_seconds)
  }
  const add = (cta: boolean) => {
    if (readOnly || pending.current) return
    const id = crypto.randomUUID()
    mutate(() => addText(editSpec, id, playheadPosition, t(cta ? 'textTrack.newCta' : 'textTrack.newText'), cta))
    onSelectText(`overlay:${id}`)
    setSelection(new Set())
  }
  const textInspector = selectedText && <TextInspector defaults={textInspectorDefaults(editSpec, selectedText)} item={selectedText} disabled={locked}
    onChange={(patch) => {
      log({ event: 'text-inspector-edit', feature: 'text-track', trace_id: `text-inspector:${projectId}:${nodeId}`, project_id: projectId, node_id: nodeId, textKey: selectedText.key, appearance: patch.style?.appearance_preset, fields: Object.keys(patch) })
      changeText(selectedText.key, patch)
    }}
    onDelete={() => { mutate(() => deleteText(editSpec, selectedText.key)); onSelectText(null) }}
    onDuplicate={() => { const result = duplicateText(editSpec, selectedText.key, crypto.randomUUID()); mutate(() => result.spec); onSelectText(result.key) }} />
  const inspectorFields: ('in_seconds' | 'out_seconds' | 'speed' | 'volume')[] = ['in_seconds', 'out_seconds', 'speed', 'volume']
  const inspector = <fieldset disabled={locked} className="space-y-3 p-4" data-testid="editspec-inspector">
    <p className="text-sm text-muted-foreground">{t('timelineDirector.manualHelp')}</p>
    {selected ? <>
      <p className="text-base text-foreground">{selected.id}</p>
      {inspectorFields.map((field) => <label key={field} className="block text-sm text-muted-foreground">
        {t(`timelineDirector.${field}`)}
        <input key={`${selected.id}:${selected[field]}`} type="number" step="0.01"
          className={inputClass} defaultValue={selected[field] ?? 1} aria-label={t(`timelineDirector.${field}`)}
          onBlur={(event) => {
            const value = event.currentTarget.valueAsNumber
            if (value !== (selected[field] ?? 1)) updateClip(selected.id, { [field]: value })
          }} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur() }} />
      </label>)}
      <label className="flex gap-2 text-sm text-foreground"><input type="checkbox" checked={selected.muted ?? false}
        onChange={(event) => updateClip(selected.id, { muted: event.target.checked })} />{t('timelineDirector.mute')}</label>
    </> : <p className="text-sm text-muted-foreground">{t('timelineDirector.selectClip')}</p>}
  </fieldset>

  return <div className="flex h-full min-h-0 flex-col" data-testid="editspec-timeline" aria-busy={saving}>
    {inspectorTarget ? createPortal(textInspector || inspector, inspectorTarget) : textInspector || inspector}
    {error && <div role="alert" className="border-b border-destructive/50 p-2 text-sm text-destructive">{error}</div>}
    <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-surface-0 px-3" data-testid="text-track-toolbar">
      <Type className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-medium text-foreground">{t('textTrack.track')}</span>
      <button type="button" disabled={locked} onClick={() => add(false)} className="ml-auto flex h-7 items-center gap-1 rounded-lg border border-input-border bg-surface-1 px-2 text-sm text-foreground hover:bg-surface-3 hover:border-input-border-hover focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"><Plus className="h-3 w-3" />{t('textTrack.add')}</button>
      <button type="button" disabled={locked} onClick={() => add(true)} className="h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-sm text-foreground hover:bg-surface-3 hover:border-input-border-hover focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40">{t('textTrack.addCta')}</button>
    </div>
    <div className="min-h-0 flex-1">
      <TimelineEditor hasTextSelection={!!selectedText} timelineState={previewState} readOnly={locked}
        leadingTracks={{ height: textLanes.length * TEXT_LANE_HEIGHT,
          header: textLanes.map((_lane, index) => <div key={index} className="flex shrink-0 items-center gap-2 border-b border-border-subtle px-2 text-xs font-medium text-foreground" style={{ height: TEXT_LANE_HEIGHT }}><Type className="h-3 w-3" />{t('textTrack.lane', { index: index + 1 })}</div>),
          content: <TextTrackLanes revision={revision} lanes={textLanes} zoom={zoomLevel} selectedKey={selectedTextKey} disabled={locked} onSelect={selectText} onChange={changeText} onGestureStart={beginTextGesture} onGestureEnd={finishTextGesture} />,
        }}
        onSelectClip={(id) => { onSelectText(null); setSelection(id ? new Set([id]) : new Set()) }}
        onToggleClipSelection={(id) => { onSelectText(null); setSelection((previous) => { const next = new Set(previous); if (next.has(id)) next.delete(id); else next.add(id); return next }) }}
        onClearSelection={() => { setSelection(new Set()); onSelectText(null) }}
        textTracksOnly
        onAddTrack={(type) => { if (type === 'text') add(false) }}
        onSetPlayhead={onSetPlayhead} onSetZoom={onSetZoom}
        onReorderClip={(id, order) => mutate(() => reorderEditSpecClip(editSpec, id, order))}
        onClipOffsetChange={(id, offset) => {
          const others = state.clips.filter((clip) => clip.id !== id)
          const order = others.filter((clip) => clip.startOffset + clip.effectiveDuration / 2 < offset).length
          mutate(() => reorderEditSpecClip(editSpec, id, order))
        }}
        onTrimClipPreview={(id, start, end) => setTrimPreview({ id, start, end })}
        onTrimClip={(id, start, end) => {
          setTrimPreview(null)
          const clip = state.clips.find((item) => item.id === id)
          if (clip) updateClip(id, { in_seconds: start, out_seconds: clip.originalDuration - end })
        }}
        onAudioOffsetChange={(id, offset) => mutate(() => ({ ...editSpec, audio: { ...editSpec.audio,
          tracks: editSpec.audio?.tracks?.map((track) => track.id === id ? { ...track, start_seconds: offset,
            ...(track.end_seconds === undefined ? {} : { end_seconds: track.end_seconds + offset - track.start_seconds }) } : track),
        } }))}
        onAudioTrimClip={(id, start, end, offset) => mutate(() => {
          if (start !== 0) throw new Error(t('timelineDirector.audioStartUnsupported'))
          const clip = state.audioClips.find((item) => item.id === id)
          if (!clip) throw new Error(`Unknown audio clip ${id}`)
          return { ...editSpec, audio: { ...editSpec.audio, tracks: editSpec.audio?.tracks?.map((track) => track.id === id
            ? { ...track, start_seconds: offset ?? track.start_seconds, end_seconds: (offset ?? track.start_seconds) + clip.originalDuration - end } : track) } }
        })}
        onSplitSelectedClip={() => mutate(() => {
          const clip = state.clips.find((item) => item.id === selectedId)
          if (!clip) throw new Error(t('timelineDirector.selectVideo'))
          return splitEditSpecClip(editSpec, clip.id, clip.trimStart + (playheadPosition - clip.startOffset) * clip.speed, crypto.randomUUID())
        })}
        onDeleteSelectedClip={() => { if (selectedText) { mutate(() => deleteText(editSpec, selectedText.key)); onSelectText(null) } else deleteSelected() }}
        onUndo={() => { const next = past[past.length - 1]; if (next) void commit(next, { past: past.slice(0, -1), future: [editSpec, ...future] }) }}
        onRedo={() => { const next = future[0]; if (next) void commit(next, { past: [...past, editSpec], future: future.slice(1) }) }}
        canUndo={past.length > 0} canRedo={future.length > 0}
      />
    </div>
    {overlays.length > 0 && <div className="max-h-32 overflow-auto border-t border-border bg-surface-0" data-testid="editspec-overlay-tracks">
      {overlays.map((overlay) => <div key={overlay.id} className="flex h-7 items-center gap-2 px-3 text-xs font-medium text-muted-foreground">
        <span className="w-24 shrink-0 truncate">{overlay.label}</span>
        <div className="relative h-5 shrink-0" style={{ width: Math.max(300, state.totalDuration * zoomLevel) }}>
          <div className="absolute h-5 overflow-hidden rounded border border-input-border bg-surface-2 px-1 text-foreground"
            style={{ left: overlay.start_seconds * zoomLevel, width: Math.max(1, overlay.duration_seconds * zoomLevel) }}>
            {overlay.label}
          </div>
        </div>
      </div>)}
    </div>}
  </div>
})
